import { useState } from 'react';

import PropTypes from 'prop-types';
import { HiPlus } from 'react-icons/hi2';

import Button from '#globalComponents/Button/Button';
import TextInput from '#globalComponents/Forms/TextInput';
import Panel from '#globalComponents/Overlay/Panel';
import Popover from '#globalComponents/Overlay/Popover';

/**
 * Build the greeting token. Matches the live syntax (origin/master EmailsTab.js): prewordM/prewordF
 * are written only when they differ from the channel default, style only when not 'formal', and
 * fallback whenever non-empty. `braces` controls single ({greeting}) vs double ({{greeting}}).
 * The backend greetingBuilder parses these |-separated key=value params.
 * @param {{ style, prewordM, prewordF, fallback }} cfg
 * @param {{ prewordM: string, prewordF: string }} defaults - channel default prewords
 * @param {'single'|'double'} braces
 * @returns {string}
 */
function buildGreetingToken({ style, prewordM, prewordF, fallback }, defaults, braces) {
	const parts = [];
	if (prewordM && prewordM !== defaults.prewordM) parts.push(`prewordM=${prewordM}`);
	if (prewordF && prewordF !== defaults.prewordF) parts.push(`prewordF=${prewordF}`);
	if (style !== 'formal') parts.push(`style=${style}`);
	if (fallback) parts.push(`fallback=${fallback}`);
	const inner = parts.length > 0 ? `greeting|${parts.join('|')}` : 'greeting';
	return braces === 'double' ? `{{${inner}}}` : `{${inner}}`;
}

/**
 * "Begrüßung konfigurieren" — a pill that opens a popover to configure a smart greeting
 * ({greeting} merge token): formal/informal style, the preword per salutation (Herr/Frau), and
 * a fallback for recipients without salutation/name (e.g. company recipients). Inserts a token
 * the backend greetingBuilder understands. The email output format is live — keep it in sync.
 *
 * @param {object} props
 * @param {(token: string) => void} props.onInsert
 * @param {'single'|'double'} [props.braces] - 'single' for email, 'double' for postmailing
 * @param {'left'|'right'} [props.align] - popover edge to anchor on; 'right' opens leftwards
 *   (use in right-side panels like the postmailing inspector to avoid overflow)
 * @param {string} [props.defaultPrewordM] - default male preword (email: "Hallo", post: "Sehr geehrter")
 * @param {string} [props.defaultPrewordF] - default female preword
 * @param {string} [props.defaultFallback] - initial fallback value
 */
const GreetingConfigurator = ({
	onInsert,
	braces = 'single',
	align = 'left',
	defaultPrewordM = 'Hallo',
	defaultPrewordF = 'Hallo',
	defaultFallback = 'Hallo',
}) => {
	const [open, setOpen] = useState(false);
	const [style, setStyle] = useState('formal');
	const [prewordM, setPrewordM] = useState(defaultPrewordM);
	const [prewordF, setPrewordF] = useState(defaultPrewordF);
	const [fallback, setFallback] = useState(defaultFallback);

	const token = buildGreetingToken(
		{ style, prewordM, prewordF, fallback },
		{ prewordM: defaultPrewordM, prewordF: defaultPrewordF },
		braces,
	);

	const confirm = () => {
		onInsert(token);
		setOpen(false);
	};

	const trigger = (
		<button
			type="button"
			onClick={() => setOpen((v) => !v)}
			className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-primary-100 border border-primary-200 rounded-control text-primary-700 hover:bg-primary-100 transition-colors"
			title="Begrüßung konfigurieren"
		>
			<HiPlus className="w-3 h-3" />
			Begrüßung
		</button>
	);

	return (
		<Popover
			open={open}
			onClose={() => setOpen(false)}
			trigger={trigger}
			size="s"
			align={align === 'right' ? 'end' : 'start'}
		>
			<Panel.Header>Begrüßung konfigurieren</Panel.Header>
			<Panel.Body>
				<label className="block text-[11px] text-bgBase-800 mb-1">Stil</label>
				<div className="flex gap-1 mb-1">
					<button
						type="button"
						onClick={() => setStyle('formal')}
						className={`flex-1 px-2 py-1 text-[11px] rounded-md border transition-colors ${
							style === 'formal'
								? 'bg-primary-100 border-primary-300 text-primary-700'
								: 'bg-white border-gray-200 text-text hover:bg-gray-50'
						}`}
					>
						Formell
					</button>
					<button
						type="button"
						onClick={() => setStyle('informal')}
						className={`flex-1 px-2 py-1 text-[11px] rounded-md border transition-colors ${
							style === 'informal'
								? 'bg-primary-100 border-primary-300 text-primary-700'
								: 'bg-white border-gray-200 text-text hover:bg-gray-50'
						}`}
					>
						Informell
					</button>
				</div>
				<p className="text-[10px] text-bgBase-800 mb-2">
					{style === 'formal' ? 'z.B. "Hallo Herr Müller"' : 'z.B. "Hallo Max"'}
				</p>

				<div className="grid grid-cols-2 gap-2 mb-2">
					<div>
						<label className="block text-[11px] text-bgBase-800 mb-1">Anrede Herr</label>
						<TextInput value={prewordM} onChange={(e) => setPrewordM(e.target.value)} />
					</div>
					<div>
						<label className="block text-[11px] text-bgBase-800 mb-1">Anrede Frau</label>
						<TextInput value={prewordF} onChange={(e) => setPrewordF(e.target.value)} />
					</div>
				</div>

				<label className="block text-[11px] text-bgBase-800 mb-1">Fallback (wenn Daten fehlen)</label>
				<TextInput
					value={fallback}
					onChange={(e) => setFallback(e.target.value)}
					placeholder="z.B. Sehr geehrte Damen und Herren"
				/>
				<p className="text-[10px] text-bgBase-800 mt-1">
					Wird genutzt, wenn der Empfänger keine Anrede/keinen Namen hat (z.B. Firmen ohne Ansprechpartner).
				</p>

				<p className="text-[10px] text-bgBase-800 mt-2">Vorschau:</p>
				<code className="block text-[10px] text-text bg-bgBase-200 border border-bgBase-400 rounded-control px-2 py-1 mt-0.5 break-all">
					{token}
				</code>
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button onClick={confirm} size="sm">
					Einfügen
				</Button>
			</Panel.Footer>
		</Popover>
	);
};

GreetingConfigurator.propTypes = {
	onInsert: PropTypes.func.isRequired,
	braces: PropTypes.oneOf(['single', 'double']),
	align: PropTypes.oneOf(['left', 'right']),
	defaultPrewordM: PropTypes.string,
	defaultPrewordF: PropTypes.string,
	defaultFallback: PropTypes.string,
};

export default GreetingConfigurator;
